import { useRef, useEffect } from 'react';

/**
 * Reusable vertical rotating wheel picker.
 * Props: options [{ value, label }], value (selected), onChange(value), height, itemHeight, placeholder (empty state text)
 */
export default function WheelPicker({ options = [], value, onChange, height = 200, itemHeight = 44, placeholder = 'No options' }) {
  const scrollRef = useRef(null);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    if (!scrollRef.current || options.length === 0 || value == null) return;
    const idx = options.findIndex((o) => o.value === value);
    if (idx < 0) return;
    scrollRef.current.scrollTop = idx * itemHeight;
  }, [options, value, itemHeight]);

  const handleScroll = () => {
    if (!scrollRef.current || options.length === 0) return;
    isScrollingRef.current = true;
    const scrollTop = scrollRef.current.scrollTop;
    const idx = Math.round(scrollTop / itemHeight);
    const clamped = Math.max(0, Math.min(idx, options.length - 1));
    const selected = options[clamped];
    if (selected && selected.value !== value) {
      onChange(selected.value);
    }
  };

  const handleScrollEnd = () => {
    isScrollingRef.current = false;
    if (!scrollRef.current || options.length === 0) return;
    const scrollTop = scrollRef.current.scrollTop;
    const idx = Math.round(scrollTop / itemHeight);
    const clamped = Math.max(0, Math.min(idx, options.length - 1));
    scrollRef.current.scrollTo({ top: clamped * itemHeight, behavior: 'smooth' });
    const selected = options[clamped];
    if (selected && selected.value !== value) {
      onChange(selected.value);
    }
  };

  let scrollEndTimer;
  const debouncedScrollEnd = () => {
    clearTimeout(scrollEndTimer);
    scrollEndTimer = setTimeout(handleScrollEnd, 150);
  };

  if (options.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500 text-sm border border-white/10 rounded"
        style={{ height: `${height}px` }}
      >
        {placeholder}
      </div>
    );
  }

  const padding = (height - itemHeight) / 2;

  return (
    <div className="relative" style={{ height: `${height}px` }}>
      {/* Fade edges */}
      <div
        className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-12 bg-gradient-to-b from-black to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-0 right-0 bottom-0 z-10 h-12 bg-gradient-to-t from-black to-transparent"
        aria-hidden
      />
      {/* Center highlight */}
      <div
        className="pointer-events-none absolute left-0 right-0 z-10 border-y border-white/20"
        style={{ top: padding, height: `${itemHeight}px` }}
        aria-hidden
      />
      <div
        ref={scrollRef}
        role="listbox"
        aria-label="Select value"
        className="overflow-y-auto overflow-x-hidden scroll-smooth border border-white/10 rounded"
        style={{
          height: `${height}px`,
          scrollSnapType: 'y mandatory',
          scrollPaddingTop: padding,
          scrollPaddingBottom: padding,
        }}
        onScroll={() => {
          handleScroll();
          debouncedScrollEnd();
        }}
      >
        <div style={{ height: `${padding}px`, scrollSnapAlign: 'start' }} />
        {options.map((opt) => (
          <div
            key={String(opt.value)}
            role="option"
            aria-selected={value === opt.value}
            aria-label={opt.label}
            onClick={() => {
              const idx = options.findIndex((o) => o.value === opt.value);
              if (idx >= 0 && scrollRef.current) {
                scrollRef.current.scrollTo({ top: idx * itemHeight, behavior: 'smooth' });
                onChange(opt.value);
              }
            }}
            className="flex items-center justify-center cursor-pointer transition select-none"
            style={{
              height: `${itemHeight}px`,
              scrollSnapAlign: 'center',
            }}
          >
            <span
              className={`text-center px-2 ${
                value === opt.value ? 'font-bold text-white text-lg' : 'text-gray-400 text-sm'
              }`}
            >
              {opt.label}
            </span>
          </div>
        ))}
        <div style={{ height: `${padding}px`, scrollSnapAlign: 'end' }} />
      </div>
    </div>
  );
}
